<template>
    <Card>
        <CardHeader>
            <div class="flex items-center gap-2">
                <ShieldCheck class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Two Factor Authentication
                </CardTitle>
            </div>
        </CardHeader>

        <CardContent class="space-y-6">
            <div class="space-y-3">
                <p class="text-xs leading-4 text-muted-foreground max-w-[411px]">
                    Add an extra layer of security to your account using an authenticator app or passkeys. When 2FA is enabled, signing in with your password or a social account asks for a second step.
                </p>
                <p class="flex items-center gap-2 text-sm font-medium">
                    <template v-if="twoFactorEnabled">
                        <ShieldCheck class="size-4 text-new-green-600" />
                        <span class="text-new-green-600">2FA enabled</span>
                    </template>
                    <template v-else>
                        <TriangleAlert class="size-4 text-new-amber-600" />
                        <span class="text-foreground">2FA not enabled</span>
                    </template>
                </p>
            </div>

            <div class="divide-y divide-border rounded-lg border border-border">
                <div class="flex flex-col gap-4 p-4">
                    <div class="flex items-center justify-between gap-4">
                        <div class="min-w-0 space-y-1">
                            <div class="flex items-center gap-1.5">
                                <p class="text-sm font-medium text-foreground">Authenticator app</p>
                                <TooltipComponent>
                                    <TooltipTrigger as-child>
                                        <Info class="size-3.5 text-muted-foreground" />
                                    </TooltipTrigger>
                                    <TooltipContent class="max-w-xs">
                                        Apps like Google Authenticator, 1Password or Authy show a new 6-digit code every 30 seconds.
                                    </TooltipContent>
                                </TooltipComponent>
                            </div>
                            <p class="text-xs text-muted-foreground">
                                <template v-if="authenticatorEnabled">Sign-ins accept a time-based code from your authenticator app.</template>
                                <template v-else>Set up an authenticator app to use time-based codes for 2FA.</template>
                            </p>
                        </div>

                        <component
                            :is="confirmComponent"
                            v-if="!authenticatorEnabled"
                            :allow-passkey="hasPasskeys"
                            @confirmed="showTwoFactorAuthentication = true"
                        >
                            <Button variant="outline" :disabled="loading">Setup</Button>
                        </component>
                        <component
                            :is="confirmComponent"
                            v-else
                            :allow-passkey="hasPasskeys"
                            @confirmed="disableTwoFactor"
                        >
                            <Button variant="outline" :disabled="loading">Disable</Button>
                        </component>
                    </div>

                    <component
                        :is="confirmComponent"
                        v-if="authenticatorEnabled"
                        :allow-passkey="hasPasskeys"
                        @confirmed="showPairingCodes = true"
                    >
                        <Button variant="outline" size="sm">Pairing QR code</Button>
                    </component>
                </div>

                <div class="flex items-center justify-between gap-4 p-4">
                    <div class="min-w-0 space-y-1" :class="{ 'opacity-50': !hasPasskeys }">
                        <div class="flex items-center gap-1.5">
                            <p class="text-sm font-medium text-foreground">Passkeys</p>
                            <TooltipComponent>
                                <TooltipTrigger as-child>
                                    <Info class="size-3.5 text-muted-foreground" />
                                </TooltipTrigger>
                                <TooltipContent class="max-w-xs">
                                    Confirm the sign-in with Touch ID, Face ID, Windows Hello or a security key. Manage your passkeys below.
                                </TooltipContent>
                            </TooltipComponent>
                        </div>
                        <p class="text-xs text-muted-foreground">
                            <template v-if="!hasPasskeys">No passkeys set up. Add a passkey to use this method for 2FA.</template>
                            <template v-else-if="passkeyTwoFactorEnabled">Sign-ins ask for one of your passkeys.</template>
                            <template v-else>Use one of your passkeys as the second step when you sign in.</template>
                        </p>
                    </div>

                    <component
                        :is="confirmComponent"
                        v-if="hasPasskeys"
                        :allow-passkey="true"
                        :force-confirmation="hasPassword && !passkeyTwoFactorEnabled"
                        @confirmed="togglePasskeyTwoFactor"
                    >
                        <Switch
                            :model-value="passkeyTwoFactorEnabled"
                            :disabled="passkeyLoading"
                            aria-label="Use passkeys for two-factor authentication"
                        />
                    </component>
                    <Switch
                        v-else
                        :model-value="false"
                        disabled
                        aria-label="Use passkeys for two-factor authentication"
                    />
                </div>
            </div>

            <component
                :is="confirmComponent"
                v-if="twoFactorEnabled"
                :allow-passkey="hasPasskeys"
                @confirmed="fetchAndShowRecoveryCodes"
            >
                <Button variant="outline">Show recovery codes</Button>
            </component>
        </CardContent>
    </Card>

    <TwoFactorAuthenticationDialogV2
        v-if="showTwoFactorAuthentication"
        :has-password="hasPassword"
        :has-passkeys="hasPasskeys"
        @close="showTwoFactorAuthentication = false"
        @enabled="onAuthenticatorEnabled"
    />
    <TwoFactorRecoveryCodesDialogV2
        v-if="showRecoveryCodes"
        title="Recovery Codes"
        :recovery-codes="fetchedRecoveryCodes"
        :can-regenerate="true"
        @close="showRecoveryCodes = false"
    />
    <TwoFactorPairingCodesDialogV2
        v-if="showPairingCodes"
        @close="showPairingCodes = false"
    />
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import axios from 'axios';
import { toast } from 'vue-sonner';
import { route } from 'ziggy-js';
import { Info, ShieldCheck, TriangleAlert } from 'lucide-vue-next';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { TooltipComponent, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import ConfirmsPassword from './ConfirmsPasswordV2.vue';
import ConfirmsOtp from './ConfirmsOtpV2.vue';
import TwoFactorAuthenticationDialogV2 from './TwoFactorAuthenticationDialogV2.vue';
import TwoFactorRecoveryCodesDialogV2 from './TwoFactorRecoveryCodesDialogV2.vue';
import TwoFactorPairingCodesDialogV2 from './TwoFactorPairingCodesDialogV2.vue';

const props = withDefaults(defineProps<{
    hasPassword: boolean;
    hasPasskeys?: boolean;
}>(), {
    hasPasskeys: false,
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);

const confirmComponent = computed(() =>
    props.hasPassword ? ConfirmsPassword : ConfirmsOtp
);

const loading = ref(false);
const passkeyLoading = ref(false);
const enabled = ref(false);
const showTwoFactorAuthentication = ref(false);
const showRecoveryCodes = ref(false);
const showPairingCodes = ref(false);
const fetchedRecoveryCodes = ref<string[]>([]);

const authenticatorEnabled = computed(() => {
    return globalProps.value.user.two_factor_authenticator_enabled || enabled.value;
});

const passkeyTwoFactorEnabled = computed(() => globalProps.value.user.two_factor_passkey_enabled);

const twoFactorEnabled = computed(() => authenticatorEnabled.value || passkeyTwoFactorEnabled.value);

const reloadUser = () => router.reload({ only: ['user'] });

const onAuthenticatorEnabled = () => {
    enabled.value = true;
    reloadUser();
};

const fetchAndShowRecoveryCodes = async () => {
    try {
        const res = await axios.get('/user/two-factor-recovery-codes');
        fetchedRecoveryCodes.value = res.data;
        showRecoveryCodes.value = true;
    } catch {
        toast.error('Failed to fetch recovery codes');
    }
};

// Turning it on needs the typed password (or a passkey confirmed just now), not just a recent confirmation
const togglePasskeyTwoFactor = async (password?: string) => {
    if (passkeyLoading.value) return;

    passkeyLoading.value = true;

    try {
        if (passkeyTwoFactorEnabled.value) {
            await axios.delete(route('auth.passkey-two-factor.destroy'));
            toast.success('Passkeys are no longer used for 2FA');
        } else {
            const res = await axios.post<{ recovery_codes: string[] | null }>(route('auth.passkey-two-factor.store'), {
                password: password || null,
            });
            toast.success('Passkeys are now used for 2FA');

            if (res.data.recovery_codes) {
                fetchedRecoveryCodes.value = res.data.recovery_codes;
                showRecoveryCodes.value = true;
            }
        }

        reloadUser();
    } catch (error) {
        const errors = axios.isAxiosError(error) ? error.response?.data?.errors : null;
        const message = errors ? (Object.values(errors).flat()[0] as string) : null;
        toast.error(message || 'Failed to update passkey 2FA');
    } finally {
        passkeyLoading.value = false;
    }
};

const disableTwoFactor = () => {
    loading.value = true;
    router.delete('/user/two-factor-authentication', {
        preserveScroll: true,
        onSuccess: () => {
            loading.value = false;
            enabled.value = false;
            toast.success('Authenticator app disabled');
        },
        onError: () => {
            loading.value = false;
            toast.error('Failed to disable the authenticator app');
        },
    });
};
</script>
